import {Auth0Provider, useAuth0} from "@auth0/auth0-react";
import {PropsWithChildren} from "react";
import {Outlet} from "react-router-dom";
import {Auth0Bridge} from "../Auth0Bridge/Auth0Bridge";
import {BootstrapGate} from "../BootstrapGate/BootstrapGate";
import {getAuthToken, getAuthUser} from "../../store/auth/auth-store";
import {AuthProvider} from "@ots-frontend/ots-ui";

export const HubGoAuthProvider = () => {

  return (
    <Auth0Provider
      domain={import.meta.env.VITE_AUTH0_DOMAIN}
      clientId={import.meta.env.VITE_AUTH0_CLIENT_ID}
      authorizationParams={{
        redirect_uri: window.location.origin,
        scope: 'openid profile email',
        audience: import.meta.env.VITE_AUTH0_AUDIENCE,
      }}
      cacheLocation="localstorage"
      useRefreshTokens={true}
    >
      <Auth0Bridge>
        <BootstrapGate>
          <AppAuthProvider>
            <Outlet/>
          </AppAuthProvider>
        </BootstrapGate>
      </Auth0Bridge>
    </Auth0Provider>
  );
}

/**
 * `AuthProvider`, told whether this session came through single sign-on so the profile can say the
 * password is changed there. It sits inside `Auth0Provider`, which is the only place that can tell.
 */
const AppAuthProvider = ({children}: PropsWithChildren) => {
  const {isAuthenticated} = useAuth0();

  return (
    <AuthProvider
      getAuthToken={getAuthToken}
      getAuthUser={getAuthUser}
      generatePermissions={permissionGenerator}
      ssoAuthenticated={isAuthenticated}>
      {children}
    </AuthProvider>
  );
}

const permissionGenerator = (userType: string | undefined): string[] => {
  const basePermissions = [
    "view",
    "view_cost",
    "invoice_view",
    "settlement_view",
    "site_assignment_view",
    "site_assignment_site_view",
    "site_assignment_company_view",
    "site_mileage_view",
    "meter_view",
    "change_log_view",
    "pipeline_view",
    "pipelineFlow_view",
    "pipelineFlowTransport_view",
    "productionVolume_view",
    "containerCluster_view",
    "container_view",
    "route_view",
    "site_view",
    "well_view",
    "person_view",
    "geofence_view",
    "geofencesitegroup_view",
  ]

  if (!userType || userType === 'RESTRICTED') {
    return [...basePermissions]
  }

  const permissions = [
    ...basePermissions,
    "edit",
    "dispatchrequest_create",
    "dispatchrequest_edit",
    "site_assignment_edit",
    "restriction_create",
    "restriction_view",
    "restriction_edit",
  ]

  if (userType === 'ADMIN') {
    permissions.push('container_edit')
    permissions.push('region_edit')
    permissions.push('view_tools')
    permissions.push('view_system_process_executions')
    permissions.push('object_mapping_view')
    permissions.push('object_mapping_create')
    permissions.push('object_mapping_edit')
    permissions.push("site_mileage_edit")
    permissions.push("meter_create")
    permissions.push("meter_edit")
    permissions.push("containerCluster_create")
    permissions.push("containerCluster_edit")
    permissions.push("containerCluster_delete")
  }

  return permissions
}
